import { Link, useParams } from 'react-router-dom';
import { useTopic } from '../api/hooks';
import type { TopicItem } from '../api/types';
import { TypeTag } from '../components/Badges';
import { ErrorPanel, Loading } from '../components/ErrorPanel';
import { strings } from '../strings';

function ItemLinks({ tenant, items }: { tenant: string; items: TopicItem[] }) {
  return (
    <ul className="m-0 list-none space-y-3 p-0">
      {items.map((item) => (
        <li key={item.id} className="card">
          <TypeTag type={item.type} />
          <h4 className="m-0 mt-1 text-body">
            <Link to={`/t/${encodeURIComponent(tenant)}/items/${encodeURIComponent(item.id)}`}>{item.name || item.id}</Link>
          </h4>
          {item.description && <p className="m-0 mt-1 line-clamp-2">{item.description}</p>}
        </li>
      ))}
    </ul>
  );
}

export function Topic() {
  const { tenant = '', id = '' } = useParams();
  const topic = useTopic(tenant, id);

  if (topic.isPending) return <Loading />;
  if (topic.isError) return <ErrorPanel error={topic.error} onRetry={() => void topic.refetch()} />;
  const t = topic.data;

  return (
    <article className="space-y-8">
      <header>
        <h1 className="m-0">{t.name}</h1>
        {t.description && <p className="max-w-3xl">{t.description}</p>}
      </header>

      {(t.objectives || t.requirements) && (
        <div className="grid gap-4 md:grid-cols-2">
          {t.objectives && (
            <section className="card">
              <h5 className="m-0">{strings.topic.objectives}</h5>
              <p className="mb-0 whitespace-pre-line">{t.objectives}</p>
            </section>
          )}
          {t.requirements && (
            <section className="card">
              <h5 className="m-0">{strings.topic.requirements}</h5>
              <p className="mb-0 whitespace-pre-line">{t.requirements}</p>
            </section>
          )}
        </div>
      )}

      {t.sequences.length > 0 && (
        <section aria-labelledby="sequences-title">
          <h2 id="sequences-title">{strings.topic.sequences}</h2>
          <div className="space-y-6">
            {t.sequences.map((s) => (
              <div key={s.id}>
                <h3 className="mb-1">{s.sequenceName}</h3>
                {s.description && <p className="mt-0">{s.description}</p>}
                {s.knowledgeItems && s.knowledgeItems.length > 0 && <ItemLinks tenant={tenant} items={s.knowledgeItems} />}
              </div>
            ))}
          </div>
        </section>
      )}

      <section aria-labelledby="items-title">
        <h2 id="items-title">{strings.topic.items}</h2>
        {t.knowledgeItems.length === 0 ? <p>{strings.topic.empty}</p> : <ItemLinks tenant={tenant} items={t.knowledgeItems} />}
      </section>
    </article>
  );
}
